// Port of Weather/METNorwayWeatherProvider.swift: MET Norway Locationforecast 2.0 (`complete`), TrailBivvy's weather
// source (iOS DECISION.md #39). Data © The Norwegian Meteorological Institute (MET Norway), CC BY 4.0 / NLOD 2.0;
// credited "Weather data from MET Norway" next to every forecast via `provenance`.
//
// ## Request
// - The PWA never calls api.met.no, and never calls the relay (weather.trailbivvy.com) cross-origin: the relay sends no
//   CORS headers by design. It calls its own origin, `GET /api/weather/locationforecast/2.0/complete?lat=46.95&lon=7.45
//   &altitude=540`, which the PWA's Cloudflare Worker forwards to the relay (iOS DECISION.md #56); the relay forwards
//   to MET with its identifying User-Agent and caches per MET's `Expires`. No fallback to api.met.no.
// - Coordinates rounded to 2 decimals (WeatherLocationPrecision), altitude to 10 m (omitted when unknown or outside
//   `altitudeRange`). MET adjusts the temperatures to that altitude.
// - No User-Agent: a web page cannot set it. `userAgent(appVersion)` is kept for the Worker that forwards to the relay.
// - Throttling: a 429 is never retried in-line (`noThrottledRetry`); after a 429 or 403 no request is sent for
//   `throttlePause` / `forbiddenPause` (or a longer Retry-After), and after the relay's 503 + Retry-After for that
//   long.
// - `Expires` is honoured: until then the stored answer is reused (its original `fetchedAt` is kept). Afterwards the
//   request carries `If-Modified-Since` = the previous `Last-Modified`; a 304 re-serves the stored body with
//   `fetchedAt` = the time it was confirmed (METNorwayResponseStore).
//
// ## Mapping (Locationforecast → WeatherForecast)
// Hourly snapshots come from the steps that have `next_1_hours` (about the first 2½ days). Temperatures in °C, wind
// m/s × 3.6 → km/h (gust undefined when MET gives none — never replaced by the mean wind), precipitation amount of the
// hour (probability only when MET gives it for that hour), percentages → 0…1, condition from `symbol_code`, thunder
// risk via METThunderRiskModel (`probability_of_thunder`, else "thunder" symbols, else unknown), no freezing level.
// Daily values per local day of `timeZone` (default: the device's): each period belongs to the day of its midpoint,
// and a day is kept only when its periods are contiguous and cover it from midnight to midnight to within half a
// period (the first day may start at the current hour). Unexpected units are rejected, never guessed.

import type {
  DailyWeather,
  DataProvenance,
  EpochMs,
  GeoCoordinate,
  WeatherForecast,
  WeatherSnapshot,
} from '../core/models';
import { isValidCoordinate } from '../core/geo';
import { parseDate } from '../core/jsonCoding';
import { headerValue, makeHTTPRequest, responseText, type HTTPClient, type HTTPResponse } from '../net/httpTypes';
import { RetryAfter, RetryPolicies } from '../net/retryPolicy';
import { WeatherLocationPrecision } from './lapseRate';
import { METNorwayResponseStore } from './metNorwayResponseStore';
import { METSymbolCode, METThunderRiskModel, ThunderstormRiskModel } from './weatherCodes';
import { WeatherFailure, weatherErrorFrom, type WeatherError, type WeatherProvider } from './weatherProvider';
import { addDays, deviceTimeZone, startOfDay } from './zonedDay';

export interface METNorwayWeatherProviderOptions {
  /** HTTP client (retries and connectivity checks are its job). */
  client: HTTPClient;
  /** Validator store for Expires / If-Modified-Since (default: persisted in IndexedDB). */
  responseStore?: METNorwayResponseStore;
  /** Locationforecast `complete` URL (default: the PWA's same-origin Worker route); injectable for tests. */
  endpoint?: string;
  /** IANA time zone of the daily values (default: the device's). */
  timeZone?: string;
  /** Clock stamping `fetchedAt` and evaluating `Expires`. */
  now?: () => EpochMs;
}

const HOUR = 3_600_000;

interface Period {
  start: EpochMs;
  end: EpochMs;
  symbol?: string;
  details: Record<string, number>;
  instant: Record<string, number>;
}

function fraction(percent: number): number {
  return Math.min(1, Math.max(0, percent / 100));
}

function kilometresPerHour(metresPerSecond: number): number {
  return Math.max(0, metresPerSecond) * 3.6;
}

function normalizedDegrees(degrees: number): number {
  const r = degrees % 360;
  return r < 0 ? r + 360 : r;
}

function map<T>(value: number | undefined, transform: (v: number) => T): T | undefined {
  return value === undefined ? undefined : transform(value);
}

/** Removes undefined members so forecasts compare and serialise like the Swift optionals. */
function compact<T extends object>(value: T): T {
  for (const key of Object.keys(value) as Array<keyof T>) if (value[key] === undefined) delete value[key];
  return value;
}

// MARK: Lenient wire-format reading (Swift `METLocationforecast` + `LenientNumbers` / `LenientStrings`)

class DecodingProblem extends Error {}

function payloadFailure(error: unknown): WeatherFailure {
  const detail = error instanceof Error ? error.message : String(error);
  return new WeatherFailure({ kind: 'decoding', message: `MET Norway payload: ${detail}` });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An optional nested object: absent or null → undefined; any other non-object is a decoding error. */
function optionalObject(value: unknown, path: string): Record<string, unknown> | undefined {
  if (value === undefined || value === null) return undefined;
  if (!isObject(value)) throw new DecodingProblem(`Type mismatch (expected object) at ${path}`);
  return value;
}

/** Finite numeric members by name; other members are skipped. */
function lenientNumbers(value: unknown, path: string): Record<string, number> {
  const object = optionalObject(value, path);
  const out: Record<string, number> = {};
  if (!object) return out;
  for (const [key, v] of Object.entries(object)) if (typeof v === 'number' && Number.isFinite(v)) out[key] = v;
  return out;
}

function lenientStrings(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isObject(value)) return out;
  for (const [key, v] of Object.entries(value)) if (typeof v === 'string') out[key] = v;
  return out;
}

export class METNorwayWeatherProvider implements WeatherProvider {
  readonly providerID = 'met-norway';

  /** TrailBivvy's weather relay (iOS #56). The PWA does not call it directly (no CORS); its Worker forwards to it. */
  static readonly relayEndpoint = 'https://weather.trailbivvy.com/locationforecast/2.0/complete';
  /** Endpoint the PWA calls: same-origin, forwarded to the relay by the PWA's Worker. */
  static readonly defaultEndpoint = '/api/weather/locationforecast/2.0/complete';
  /** Where the credit links to (MET Norway's API site). */
  static readonly sourceURL = 'https://api.met.no/';
  /** Contact URL in the relay-facing User-Agent. */
  static readonly contactURL = 'https://trailbivvy.com/support/';
  /** Required credit, shown verbatim next to forecasts. */
  static readonly attribution = 'Weather data from MET Norway';
  static readonly license = 'CC BY 4.0 / NLOD 2.0 (MET Norway)';
  static readonly dataset = 'MET Norway Locationforecast 2.0 (complete)';
  /** Altitudes (m) sent; values outside are omitted rather than sent. */
  static readonly altitudeRange = { min: -500, max: 9000 } as const;
  /** Longest `Expires` window honoured, seconds. */
  static readonly maxExpiryWindow = 6 * 3600;
  /** No request for this long after a 429 (or the Retry-After, if longer), seconds. */
  static readonly throttlePause = 10 * 60;
  /** No request for this long after a 403, seconds. */
  static readonly forbiddenPause = 60 * 60;
  /** Longest pause honoured, whatever Retry-After says, seconds. */
  static readonly maxPause = 6 * 3600;

  private readonly client: HTTPClient;
  private readonly store: METNorwayResponseStore;
  private readonly endpoint: string;
  private readonly timeZone: string;
  private readonly now: () => EpochMs;

  constructor(options: METNorwayWeatherProviderOptions) {
    this.client = options.client;
    this.store = options.responseStore ?? new METNorwayResponseStore();
    this.endpoint = options.endpoint ?? METNorwayWeatherProvider.defaultEndpoint;
    this.timeZone = options.timeZone ?? deviceTimeZone();
    this.now = options.now ?? Date.now;
  }

  /** `TrailBivvy/<version> (+https://trailbivvy.com/support/)`; a blank or odd version becomes `unknown`. */
  static userAgent(appVersion: string): string {
    const trimmed = appVersion.trim();
    const safe = /^[A-Za-z0-9.-]*$/.test(trimmed);
    const version = trimmed === '' || !safe ? 'unknown' : trimmed;
    return `TrailBivvy/${version} (+${METNorwayWeatherProvider.contactURL})`;
  }

  // MARK: Request

  /** The location as sent (2 decimals). */
  static transmittedLocation(location: GeoCoordinate): GeoCoordinate {
    return WeatherLocationPrecision.rounded(location);
  }

  /** The altitude as sent: rounded to 10 m; undefined when unknown, non-finite or outside `altitudeRange`. */
  static transmittedElevation(elevation: number | undefined): number | undefined {
    const rounded = WeatherLocationPrecision.roundedElevation(elevation);
    const { min, max } = METNorwayWeatherProvider.altitudeRange;
    return rounded !== undefined && rounded >= min && rounded <= max ? rounded : undefined;
  }

  static queryItems(location: GeoCoordinate, elevation: number | undefined): Array<[string, string]> {
    const sent = METNorwayWeatherProvider.transmittedLocation(location);
    const items: Array<[string, string]> = [
      ['lat', WeatherLocationPrecision.format(sent.latitude)],
      ['lon', WeatherLocationPrecision.format(sent.longitude)],
    ];
    const altitude = METNorwayWeatherProvider.transmittedElevation(elevation);
    if (altitude !== undefined) items.push(['altitude', altitude.toFixed(0)]);
    return items;
  }

  /** Store key for a request: its query, e.g. `lat=46.95&lon=7.45&altitude=540`. */
  static requestKey(location: GeoCoordinate, elevation: number | undefined): string {
    return METNorwayWeatherProvider.queryItems(location, elevation)
      .map(([name, value]) => `${name}=${value}`)
      .join('&');
  }

  /** The request URL, never more precise than the transmitted location / elevation. */
  makeURL(location: GeoCoordinate, elevation: number | undefined): string {
    const query = METNorwayWeatherProvider.requestKey(location, elevation);
    return `${this.endpoint}${this.endpoint.includes('?') ? '&' : '?'}${query}`;
  }

  async forecast(location: GeoCoordinate, elevation?: number): Promise<WeatherForecast> {
    if (!isValidCoordinate(location)) throw new WeatherFailure({ kind: 'noData' });
    const url = this.makeURL(location, elevation);
    const key = METNorwayWeatherProvider.requestKey(location, elevation);
    const sentLocation = METNorwayWeatherProvider.transmittedLocation(location);
    const sentElevation = METNorwayWeatherProvider.transmittedElevation(elevation);
    const parse = (body: string, fetchedAt: EpochMs) =>
      METNorwayWeatherProvider.parse(body, sentLocation, sentElevation, fetchedAt, this.timeZone);

    const requestTime = this.now();
    let previous = await this.store.entry(key, requestTime);

    // 1. Before `Expires`, MET asks for no new request: reuse the stored answer with its original fetchedAt.
    const unexpired =
      previous?.expiresAt !== undefined && requestTime >= previous.fetchedAt && requestTime < previous.expiresAt;
    if (previous && unexpired) {
      try {
        return parse(previous.body, previous.fetchedAt);
      } catch {
        await this.store.remove(key);
        previous = undefined;
      }
    }

    // 2. After a 429/403, MET is left alone until the pause ends (the caller falls back to its cache).
    const pausedUntil = this.store.requestsPaused(requestTime);
    if (pausedUntil !== undefined) {
      throw new WeatherFailure({
        kind: 'network',
        error: { kind: 'http', status: 429, retryAfter: (pausedUntil - requestTime) / 1000 },
      });
    }

    // 3. Conditional request when a stored answer exists; at most one unconditional retry if a 304 cannot be used.
    let conditional = previous?.lastModified !== undefined;
    for (;;) {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (conditional && previous?.lastModified !== undefined) headers['If-Modified-Since'] = previous.lastModified;
      let response: HTTPResponse;
      try {
        // Our own stores handle caching (no-store: the browser cache never hands back an old body stamped as new).
        // A 429 is never repeated within seconds (MET's terms).
        const request = makeHTTPRequest(url, {
          headers,
          cachePolicy: 'reloadIgnoringCache',
          retryPolicy: RetryPolicies.noThrottledRetry,
        });
        response = await this.client.send(request);
      } catch (error) {
        const mapped = weatherErrorFrom(error);
        this.pauseAfterThrottling(mapped);
        throw new WeatherFailure(mapped);
      }
      const receivedAt = this.now();
      const expiresAt = METNorwayWeatherProvider.expiry(response, receivedAt);

      if (response.statusCode === 304) {
        // MET confirms the stored forecast is still the current one.
        if (conditional && previous) {
          let confirmed: WeatherForecast | undefined;
          try {
            confirmed = parse(previous.body, receivedAt);
          } catch {
            confirmed = undefined;
          }
          if (confirmed) {
            await this.store.store({
              key,
              body: previous.body,
              lastModified: headerValue(response, 'Last-Modified') ?? previous.lastModified,
              expiresAt,
              fetchedAt: receivedAt,
            });
            return confirmed;
          }
        }
        await this.store.remove(key);
        if (!conditional) {
          const message = 'MET Norway answered 304 to an unconditional request';
          throw new WeatherFailure({ kind: 'decoding', message });
        }
        previous = undefined;
        conditional = false;
        continue;
      }

      if (response.statusCode === 203) {
        // MET's deprecation signal for this product version: keep working, but make it visible.
        console.error('MET Norway answered 203: Locationforecast 2.0 is deprecated; update the provider');
      }
      const body = responseText(response);
      const forecast = parse(body, receivedAt);
      const lastModified = headerValue(response, 'Last-Modified');
      await this.store.store({ key, body, lastModified, expiresAt, fetchedAt: receivedAt });
      return forecast;
    }
  }

  /** After 429 or 403 (or the relay's 503 + Retry-After), pauses all MET requests. */
  private pauseAfterThrottling(error: WeatherError): void {
    if (error.kind !== 'network' || error.error.kind !== 'http') return;
    const pause = METNorwayWeatherProvider.pause(error.error.status, error.error.retryAfter);
    if (pause === undefined) return;
    console.error(`MET Norway answered ${error.error.status}; pausing requests for ${Math.trunc(pause)} s`);
    this.store.pauseRequests(this.now() + pause * 1000);
  }

  /**
   * How long (seconds) to leave MET alone after an HTTP error, or undefined when no pause is needed. The relay answers
   * 503 with a Retry-After while MET has paused it: the app then waits exactly that long, for every place.
   */
  static pause(status: number, retryAfter: number | undefined): number | undefined {
    let base: number;
    switch (status) {
      case 429:
        base = METNorwayWeatherProvider.throttlePause;
        break;
      case 403:
        base = METNorwayWeatherProvider.forbiddenPause;
        break;
      case 503:
        if (retryAfter === undefined || !Number.isFinite(retryAfter) || retryAfter <= 0) return undefined;
        base = 0;
        break;
      default:
        return undefined;
    }
    const requested = retryAfter !== undefined && Number.isFinite(retryAfter) ? retryAfter : 0;
    return Math.min(METNorwayWeatherProvider.maxPause, Math.max(base, requested));
  }

  /**
   * Device time until which MET asks not to repeat the request: `Expires` measured against the response `Date` (so a
   * wrong device clock does not shift it), capped at `maxExpiryWindow`. Undefined when absent, malformed or past.
   */
  static expiry(response: HTTPResponse, receivedAt: EpochMs): EpochMs | undefined {
    const value = headerValue(response, 'Expires');
    const expires = value !== undefined ? RetryAfter.parseHTTPDate(value) : undefined;
    if (expires === undefined) return undefined;
    const dateHeader = headerValue(response, 'Date');
    const serverNow = (dateHeader !== undefined ? RetryAfter.parseHTTPDate(dateHeader) : undefined) ?? receivedAt;
    const window = (expires - serverNow) / 1000;
    if (!Number.isFinite(window) || window <= 0) return undefined;
    return receivedAt + Math.min(window, METNorwayWeatherProvider.maxExpiryWindow) * 1000;
  }

  // MARK: Parsing

  /**
   * Converts a Locationforecast 2.0 `complete` (or `compact`) GeoJSON body into a WeatherForecast.
   * @param location the (rounded) location that was requested; kept as the forecast location.
   * @param requestedElevation the `altitude=` sent; when undefined the altitude MET reports is used.
   * @param fetchedAt when MET delivered or confirmed the body.
   * @param timeZone IANA zone whose local days the daily values describe.
   * @throws WeatherFailure `decoding` for malformed payloads or unexpected units, `noData` without a usable hour.
   */
  static parse(
    body: string,
    location: GeoCoordinate,
    requestedElevation: number | undefined,
    fetchedAt: EpochMs,
    timeZone: string = deviceTimeZone(),
  ): WeatherForecast {
    let root: Record<string, unknown>;
    let timeseries: unknown[];
    try {
      const json: unknown = JSON.parse(body);
      if (!isObject(json)) throw new DecodingProblem('Type mismatch (expected object) at <root>');
      const properties = json.properties;
      if (!isObject(properties)) throw new DecodingProblem("Missing key 'properties' at <root>");
      if (!Array.isArray(properties.timeseries)) throw new DecodingProblem("Missing key 'timeseries' at properties");
      root = json;
      timeseries = properties.timeseries;
    } catch (error) {
      throw payloadFailure(error);
    }
    const properties = root.properties as Record<string, unknown>;
    const meta = isObject(properties.meta) ? properties.meta : undefined;
    METNorwayWeatherProvider.checkUnits(lenientStrings(meta?.units));

    const snapshots: WeatherSnapshot[] = [];
    const periods: Period[] = [];
    try {
      timeseries.forEach((rawStep, index) => {
        const path = `properties.timeseries[${index}]`;
        if (!isObject(rawStep)) throw new DecodingProblem(`Type mismatch (expected object) at ${path}`);
        if (typeof rawStep.time !== 'string') throw new DecodingProblem(`Missing key 'time' at ${path}`);
        const data = optionalObject(rawStep.data, `${path}.data`);
        if (!data) throw new DecodingProblem(`Missing key 'data' at ${path}`);
        const time = parseDate(rawStep.time);
        const instantObject = optionalObject(data.instant, `${path}.data.instant`);
        const instant = lenientNumbers(instantObject?.details, `${path}.data.instant.details`);
        const next1 = optionalObject(data.next_1_hours, `${path}.data.next_1_hours`);
        const next6 = optionalObject(data.next_6_hours, `${path}.data.next_6_hours`);
        const readPeriod = (p: Record<string, unknown>, name: string) => {
          const summary = optionalObject(p.summary, `${path}.data.${name}.summary`);
          const symbol = summary?.symbol_code;
          if (symbol !== undefined && symbol !== null && typeof symbol !== 'string') {
            throw new DecodingProblem(`Type mismatch (expected String) at ${path}.data.${name}.summary.symbol_code`);
          }
          const details = lenientNumbers(p.details, `${path}.data.${name}.details`);
          return { symbol: typeof symbol === 'string' ? symbol : undefined, details };
        };
        const p1 = next1 ? readPeriod(next1, 'next_1_hours') : undefined;
        const p6 = next6 ? readPeriod(next6, 'next_6_hours') : undefined;
        if (time === undefined) return;
        if (p1) {
          const { symbol, details } = p1;
          periods.push({ start: time, end: time + HOUR, symbol, details, instant });
          const temperature = instant.air_temperature;
          if (temperature === undefined) return;
          const thunderProbability = map(details.probability_of_thunder, fraction);
          snapshots.push(
            compact<WeatherSnapshot>({
              time,
              temperature,
              feelsLike: instant.apparent_air_temperature,
              precipitationProbability: map(details.probability_of_precipitation, fraction),
              precipitationAmount: map(details.precipitation_amount, (v) => Math.max(0, v)),
              windSpeed: map(instant.wind_speed, kilometresPerHour),
              windGust: map(instant.wind_speed_of_gust, kilometresPerHour),
              windDirection: map(instant.wind_from_direction, normalizedDegrees),
              thunderstormRisk: METThunderRiskModel.risk(symbol, thunderProbability),
              freezingLevel: undefined,
              cloudCover: map(instant.cloud_area_fraction, fraction),
              condition: symbol !== undefined ? METSymbolCode.condition(symbol) : 'unknown',
              seaLevelPressure: map(instant.air_pressure_at_sea_level, (v) => (v > 0 ? v : undefined)),
              relativeHumidity: map(instant.relative_humidity, fraction),
              fogFraction: map(instant.fog_area_fraction, fraction),
              uvIndexClearSky: map(instant.ultraviolet_index_clear_sky, (v) => Math.max(0, v)),
              thunderProbability,
            }),
          );
        } else if (p6) {
          periods.push({ start: time, end: time + 6 * HOUR, symbol: p6.symbol, details: p6.details, instant });
        }
      });
    } catch (error) {
      throw payloadFailure(error);
    }
    if (snapshots.length === 0) throw new WeatherFailure({ kind: 'noData' });
    snapshots.sort((a, b) => a.time - b.time);
    periods.sort((a, b) => a.start - b.start);

    const daily = METNorwayWeatherProvider.dailyValues(periods, timeZone);

    const coordinates = isObject(root.geometry) ? root.geometry.coordinates : undefined;
    const allNumbers = Array.isArray(coordinates) && coordinates.every((c) => typeof c === 'number');
    const numericCoordinates = allNumbers ? (coordinates as number[]) : undefined;
    const reportedAltitude = numericCoordinates && numericCoordinates.length > 2 ? numericCoordinates[2] : undefined;
    const altitude = reportedAltitude !== undefined && Number.isFinite(reportedAltitude) ? reportedAltitude : undefined;
    const requested =
      requestedElevation !== undefined && Number.isFinite(requestedElevation) ? requestedElevation : undefined;
    const updatedAt = typeof meta?.updated_at === 'string' ? parseDate(meta.updated_at) : undefined;
    return compact<WeatherForecast>({
      location: { latitude: location.latitude, longitude: location.longitude },
      elevation: requested ?? altitude,
      modelElevation: altitude,
      hourly: snapshots,
      daily,
      fetchedAt,
      issuedAt: updatedAt,
      isSynthetic: false,
      provenance: METNorwayWeatherProvider.provenance(fetchedAt, updatedAt),
    });
  }

  /** Daily values per local day (see the file header). */
  private static dailyValues(periods: Period[], timeZone: string): DailyWeather[] {
    const firstStart = periods[0]?.start;
    const byDay = new Map<EpochMs, Period[]>();
    for (const period of periods) {
      const day = startOfDay(period.start + (period.end - period.start) / 2, timeZone);
      const group = byDay.get(day);
      if (group) group.push(period);
      else byDay.set(day, [period]);
    }
    const days: DailyWeather[] = [];
    for (const dayStart of [...byDay.keys()].sort((a, b) => a - b)) {
      const dayPeriods = byDay.get(dayStart) ?? [];
      const first = dayPeriods[0];
      const last = dayPeriods[dayPeriods.length - 1];
      if (!first || !last) continue;
      const dayEnd = addDays(dayStart, 1, timeZone);
      // Later days must reach back to midnight; the first forecast day starts at the current hour.
      const firstHalf = (first.end - first.start) / 2;
      if (!(first.start === firstStart || first.start - firstHalf <= dayStart)) continue;
      let cursor = first.start;
      let contiguous = true;
      for (const period of dayPeriods) {
        if (period.start !== cursor) {
          contiguous = false;
          break;
        }
        cursor = period.end;
      }
      const lastHalf = (last.end - last.start) / 2;
      if (!contiguous || cursor + lastHalf < dayEnd) continue;

      const values = (pick: (p: Period) => number | undefined) =>
        dayPeriods.flatMap((p) => {
          const v = pick(p);
          return v === undefined ? [] : [v];
        });
      const temperatures = [
        ...values((p) => p.instant.air_temperature),
        ...values((p) => p.details.air_temperature_min),
        ...values((p) => p.details.air_temperature_max),
      ];
      const amounts = dayPeriods.map((p) => p.details.precipitation_amount);
      const gusts = dayPeriods.flatMap((p) =>
        p.instant.wind_speed_of_gust !== undefined ? [kilometresPerHour(p.instant.wind_speed_of_gust)] : [],
      );
      const risks = dayPeriods.map((p) =>
        METThunderRiskModel.risk(p.symbol, map(p.details.probability_of_thunder, fraction)),
      );
      days.push(
        compact<DailyWeather>({
          date: dayStart,
          sunrise: undefined,
          sunset: undefined,
          temperatureMin: temperatures.length ? Math.min(...temperatures) : undefined,
          temperatureMax: temperatures.length ? Math.max(...temperatures) : undefined,
          precipitationSum: amounts.some((a) => a === undefined)
            ? undefined
            : amounts.reduce<number>((sum, a) => sum + Math.max(0, a ?? 0), 0),
          maxWindGust: gusts.length ? Math.max(...gusts) : undefined,
          thunderstormRisk: ThunderstormRiskModel.dailyRisk(risks),
        }),
      );
    }
    return days;
  }

  /** Provenance recorded on every MET Norway forecast. The source URL deliberately carries no coordinates. */
  static provenance(fetchedAt: EpochMs, sourceUpdatedAt?: EpochMs): DataProvenance {
    return compact<DataProvenance>({
      provider: 'MET Norway',
      dataset: METNorwayWeatherProvider.dataset,
      sourceURL: METNorwayWeatherProvider.sourceURL,
      license: METNorwayWeatherProvider.license,
      attribution: METNorwayWeatherProvider.attribution,
      fetchedAt,
      sourceUpdatedAt,
      isSynthetic: false,
      licenseReviewStatus: 'pendingHumanReview',
    });
  }

  /** Units the mapping relies on (as documented by MET). A different unit is an error, never silently converted. */
  static readonly expectedUnits: Readonly<Record<string, string>> = {
    air_temperature: 'celsius',
    apparent_air_temperature: 'celsius',
    air_temperature_max: 'celsius',
    air_temperature_min: 'celsius',
    wind_speed: 'm/s',
    wind_speed_of_gust: 'm/s',
    wind_from_direction: 'degrees',
    precipitation_amount: 'mm',
    air_pressure_at_sea_level: 'hPa',
    cloud_area_fraction: '%',
    relative_humidity: '%',
    fog_area_fraction: '%',
    probability_of_precipitation: '%',
    probability_of_thunder: '%',
  };

  static checkUnits(units: Record<string, string>): void {
    for (const [name, expected] of Object.entries(METNorwayWeatherProvider.expectedUnits)) {
      const unit = units[name];
      if (unit !== undefined && unit.toLowerCase() !== expected.toLowerCase()) {
        const message = `MET Norway unit for ${name} is ${unit}, expected ${expected}`;
        throw new WeatherFailure({ kind: 'decoding', message });
      }
    }
  }
}
